import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Tag,
  Dropdown,
  Modal,
  Form,
  Input,
  message,
  Button,
} from "antd";
import HeadingComponent from "../../components/HeadingComponent";
import { useSelector } from "react-redux";
import CustomTable from "../../components/CustomTable";
import { CloseCircleOutlined, MoreOutlined } from "@ant-design/icons";
import DeleteModal from "../../components/DeleteModal";
import {
  getAllSubscriptions,
  updatePackage,
  updateSubscription,
} from "./APIS/subscriptionApis";
import PackagesFilter from "../../components/PackagesFilter";
import { getNameByProductId } from "./constants/packages";
import { parseFirebaseDateTime } from "../../utils/helper";
import axios from "axios";

const Subscriptions = () => {
  const selectedPackages = useSelector(
    (state) => state.packages.selectedPackages
  );

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const columns = [
    {
      title: "Package Name",
      dataIndex: "prod_id",
      render: (product_id) => {
        return getNameByProductId(product_id) || "-";
      },
    },
    {
      title: "Customer Email",
      dataIndex: "payload",
      render: (_, record) => {
        const metadata = record?.payload?.metadata?.user_details
          ? JSON.parse(record?.payload?.metadata?.user_details)
          : {};
        const payload_email = metadata?.user_email || "-";
        return record?.user_details?.user_email || payload_email;
      },
    },
    {
      title: "Start Date",
      dataIndex: "payload",
      render: (_, record) => {
        let start_date;
        if (record?.status === "active") {
          if (record?.payload?.start_date) {
            start_date = new Date(record?.payload?.start_date * 1000)
              .toString()
              .slice(0, 21);
          } else if (record?.payload?.current_period_start) {
            start_date = new Date(record?.payload?.current_period_start * 1000)
              .toString()
              .slice(0, 21);
          }
        } else {
          start_date = parseFirebaseDateTime(record?.trial_starts_at)
            ?.toString()
            .slice(0, 21);
        }
        return start_date ?? "N/A";
      },
    },

    {
      title: "End Date",
      dataIndex: "end_date",
      render: (_, record) => {
        return record?.end_date
          ? new Date(record?.end_date).toString().slice(0, 21)
          : "N/A";
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        const statusMap = {
          active: "Active",
          trialing: "Trialing",
          canceled: "Canceled",
          paused: "Paused",
          incomplete: "Incomplete",
          expired: "Expired",
        };

        const statusColors = {
          active: "green",
          trialing: "blue",
          canceled: "red",
          paused: "gray",
          expired: "yellow",
        };
        return (
          <Tag color={`${statusColors[status]}`}>
            {statusMap[status] || status}
          </Tag>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        const items = [
          {
            key: "1",
            label: "Cancel Subscription",
            icon: <CloseCircleOutlined />,
            onClick: () => {
              console.log("record", record);
              setOpenCancelModal(true);
              setSelectedRecord(record);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} placement="bottomRight">
            <MoreOutlined className="icons_style" />
          </Dropdown>
        );
      },
    },
  ];

  const getSubscriptions = async () => {
    try {
      setLoading(true);
      let response = await getAllSubscriptions(selectedPackages);
      setSubscriptions(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPackages.length > 0) {
      getSubscriptions();
    }
  }, [selectedPackages]);

  const handleCancelSubscription = async (values) => {
    try {
      setLoading(true);
      const plan_name = getNameByProductId(selectedRecord?.prod_id) || "-";
      if (selectedRecord?.subscription_id) {
        const BASE_URL = process.env.REACT_APP_BASE_API;
        let response = await axios.post(
          `${BASE_URL}payment/cancel-subscription/${selectedRecord?.subscription_id}`,
          {
            cancel_reason: values.reason,
            plan: plan_name,
            email: selectedRecord?.user_details?.user_email || "-",
          }
        );
        let updated_record = {
          cancel_reason: values.reason,
          status: "canceled",
          end_date: new Date().toString(),
          id: selectedRecord?.id,
          payload: response?.data?.data?.canceled_subscription,
        };
        await updateSubscription(updated_record);
        message.success("Subscription canceled successfully!");
      } else {
        let updated_record = {
          cancel_reason: values.reason,
          status: "canceled",
          end_date: new Date().toString(),
          id: selectedRecord?.id,
        };
        await updateSubscription(updated_record);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setOpenCancelModal(false);
    }
  };

  const onCreate = async (values) => {
    handleCancelSubscription(values);
  };

  return (
    <>
      <Row>
        <Col xs={24} sm={8}>
          <HeadingComponent text="Subcriptions" />
        </Col>

        <Col xs={24} sm={16} className="filter_col">
          <PackagesFilter />
        </Col>
      </Row>
      <br />
      <CustomTable
        dataSource={subscriptions}
        columns={columns}
        loading={loading}
      />

      {openCancelModal && (
        <Modal
          open={openCancelModal}
          okText="Cancel Subscription"
          cancelText="Cancel"
          okButtonProps={{
            autoFocus: true,
            htmlType: "submit",
            loading: loading,
          }}
          onCancel={() => setOpenCancelModal(false)}
          onOk={() => {
            form.submit();
          }}
          destroyOnHidden
          modalRender={(dom) => <>{dom}</>}
        >
          <div className="refund_modal_header">
            <p>Cancel subscription</p>
            <p>Are you sure you want to cancel this subscription?</p>
            <br />
            <Form
              layout="vertical"
              form={form}
              name="form"
              clearOnDestroy
              onFinish={(values) => onCreate(values)}
            >
              <Form.Item
                name="reason"
                label="Cancel Reason"
                initialValue={""}
                rules={[
                  {
                    required: true,
                    message: "Please input reason!",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Please enter reason for canceling subscription"
                />
              </Form.Item>
            </Form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Subscriptions;
